import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Box, Checkbox, LinearProgress, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material'
import { ui, tone } from '../../../utils/uiTokens'
import { money } from '../../../utils/salaryFormulas'
import { dateRange, ddmm } from '../../../utils/periodDates'
import { usePersistedState } from '../../../hooks/usePersistedState'
import { filterField } from '../../../components/filterSelect/FilterSelect'
import { MONTHS } from '../../../components/monthWeekFilter/MonthWeekFilter'
import { directorApi, DirHeadSalary } from '../../../features/director/directorApi'
import { useDirectorSubject } from '../../head/headScope'
import DirectorSubjectFilter from '../DirectorSubjectFilter'
import { bodyRow, headRow, muted, sub, tableWrap } from '../ui'

const HEAD_BASE = 120000

// Пән басшыларының жалақысы: оклад 120 000, бонус қолмен (әзірге бос), беру күні, берілді
const HeadSalariesTab = () => {
    const { formatMessage, locale } = useIntl()
    const [month, setMonth] = usePersistedState<number>('director.headMonth', 1)
    const { subjectId } = useDirectorSubject()
    const { data = [], isFetching } = directorApi.useGetHeadSalariesQuery({ monthNumber: month })
    const [save, { isLoading: saving }] = directorApi.useSaveHeadSalaryMutation()

    const rows = data.filter((h) => !subjectId || (h.subjects ?? []).some((x) => x.id === subjectId))
    const t = (x: { titleKz: string; titleRu: string }) => (locale === 'kz' ? x.titleKz : x.titleRu) || x.titleRu
    const base = (h: DirHeadSalary) => h.base || HEAD_BASE
    const total = (h: DirHeadSalary) => base(h) + (h.bonus || 0)
    const paid = rows.filter((h) => h.paid).length

    const update = async (h: DirHeadSalary, patch: Partial<DirHeadSalary>) => {
        try {
            await save({ ...h, base: base(h), monthNumber: month, ...patch }).unwrap()
            toast.success(formatMessage({ id: 'salarySavedToast' }))
        } catch { /* общий обработчик */ }
    }

    return (
        <>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
                <DirectorSubjectFilter />
                <Select size="small" value={month} onChange={(e) => setMonth(Number(e.target.value))} sx={{ ...filterField, width: 'auto', minWidth: 120 }}>
                    {MONTHS.map((m) => (
                        <MenuItem key={m} value={m}>{m} {formatMessage({ id: 'month' })}</MenuItem>
                    ))}
                </Select>
            </Box>
            <Box sx={{ background: ui.surface, border: `1px solid ${ui.border}`, borderRadius: '12px', padding: '10px 14px', marginBottom: '12px', ...sub }}>
                {formatMessage({ id: 'dirHeadNote' }, { base: money(HEAD_BASE) })}
            </Box>
            <Box sx={{ ...sub, marginBottom: '8px' }}>
                {formatMessage({ id: 'dirPaidCol' })}: <b>{paid} / {rows.length}</b>
            </Box>
            {(isFetching || saving) && <LinearProgress />}
            <Box sx={tableWrap}>
                <Table sx={{ minWidth: 980 }}>
                    <TableHead sx={headRow}>
                        <TableRow>
                            <TableCell>{formatMessage({ id: 'dirHead' })}</TableCell>
                            <TableCell>{formatMessage({ id: 'colSubject' })}</TableCell>
                            <TableCell align="right">{formatMessage({ id: 'colMentor' })}</TableCell>
                            <TableCell align="right">{formatMessage({ id: 'dirBase' })}</TableCell>
                            <TableCell align="right">{formatMessage({ id: 'dirBonus' })}</TableCell>
                            <TableCell align="right">{formatMessage({ id: 'headSalary' })}</TableCell>
                            <TableCell>{formatMessage({ id: 'dirPayDate' })}</TableCell>
                            <TableCell align="center">{formatMessage({ id: 'dirPaidCol' })}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((h) => (
                            <TableRow key={h.headId} sx={{ ...bodyRow, background: h.paid ? tone.ok.bg : undefined }}>
                                <TableCell><b>{h.headName || `#${h.headId}`}</b></TableCell>
                                <TableCell>{h.subjects?.map((x) => t(x)).join(', ')}</TableCell>
                                <TableCell align="right">{h.mentorCount ?? 0}</TableCell>
                                <TableCell align="right">{money(base(h))}</TableCell>
                                <TableCell align="right">
                                    <BonusInput value={h.bonus} disabled={h.paid} onSave={(bonus) => update(h, { bonus })} />
                                </TableCell>
                                <TableCell align="right"><b>{money(total(h))}</b></TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="date"
                                        value={h.payDate ?? h.dueTo ?? ''}
                                        disabled={h.paid}
                                        onChange={(e) => e.target.value && update(h, { payDate: e.target.value })}
                                        inputProps={{ style: { padding: '5px 8px', fontSize: '12.5px' } }}
                                    />
                                    <Box sx={muted}>{formatMessage({ id: 'dirDue' })} {dateRange(h.dueFrom, h.dueTo)}</Box>
                                </TableCell>
                                <TableCell align="center">
                                    <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', fontSize: '12.5px', color: tone.ok.text, fontWeight: 700 }}>
                                        <Checkbox size="small" checked={!!h.paid} disabled={saving} onChange={(e) => update(h, { paid: e.target.checked })} />
                                        {h.paid && `${formatMessage({ id: 'dirPaidShort' })} ${ddmm(h.paidAt)}`}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!isFetching && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ color: ui.muted, padding: '28px' }}>{formatMessage({ id: 'noData' })}</TableCell>
                            </TableRow>
                        )}
                        {rows.length > 0 && (
                            <TableRow sx={{ ...bodyRow, background: ui.surface }}>
                                <TableCell colSpan={2}><b>{formatMessage({ id: 'practiceTotal' })}</b></TableCell>
                                <TableCell align="right"><b>{rows.reduce((s, h) => s + (h.mentorCount ?? 0), 0)}</b></TableCell>
                                <TableCell align="right"><b>{money(rows.reduce((s, h) => s + base(h), 0))}</b></TableCell>
                                <TableCell align="right"><b>{money(rows.reduce((s, h) => s + (h.bonus || 0), 0))}</b></TableCell>
                                <TableCell align="right"><b>{money(rows.reduce((s, h) => s + total(h), 0))}</b></TableCell>
                                <TableCell />
                                <TableCell align="center"><b>{paid}/{rows.length}</b></TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>
        </>
    )
}

const BonusInput: React.FC<{ value: number | null; disabled: boolean; onSave: (v: number) => void }> = ({ value, disabled, onSave }) => {
    const [draft, setDraft] = useState<string | null>(null)
    const current = value ? String(value) : ''
    return (
        <TextField
            size="small"
            type="number"
            placeholder="—"
            value={draft ?? current}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
                if (draft != null && draft !== current) onSave(Math.max(0, Math.round(Number(draft) || 0)))
                setDraft(null)
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
            }}
            inputProps={{ min: 0, style: { textAlign: 'right', padding: '5px 8px' } }}
            sx={{ width: 110 }}
        />
    )
}

export default HeadSalariesTab
